import { useEffect, useState } from 'react';
import { WifiOff } from 'lucide-react';

/**
 * OfflineBanner — the "you are offline" notice Vandana designed on 2026-08-21.
 *
 * Shows a Sandvik Carbon Black strip at the very top of the viewport when the
 * browser reports no network. Hides itself the moment the connection returns.
 * Not dismissible — this is a fact about the world, not a message the user is
 * meant to acknowledge and clear.
 *
 * WHAT THIS DOES NOT TRY TO DO. It does not open per image, it does not
 * describe which pages are affected, and it does not narrate the fallback
 * behaviour. Product cards already render their own per-image placeholder on
 * load failure; the banner sits above the layout and says once, plainly, that
 * the app is offline. Two half-truths would be worse than one honest line.
 *
 * WHY navigator.onLine IS ENOUGH. It is a browser hint, and it can lie in
 * both directions (say online when the router is unreachable, say offline
 * when a captive portal replies). Vandana's design accepts that: a false
 * negative is invisible, and a false positive is corrected by the per-image
 * fallback that already ships. A more expensive probe would burn radio on a
 * user we know is offline.
 */
export default function OfflineBanner() {
  const [isOnline, setIsOnline] = useState(
    typeof navigator !== 'undefined' ? navigator.onLine : true,
  );

  useEffect(() => {
    const on = () => setIsOnline(true);
    const off = () => setIsOnline(false);
    window.addEventListener('online', on);
    window.addEventListener('offline', off);
    return () => {
      window.removeEventListener('online', on);
      window.removeEventListener('offline', off);
    };
  }, []);

  if (isOnline) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="w-full bg-carbon-black text-white px-4 py-2 flex items-center gap-3 text-sm"
    >
      <WifiOff size={16} aria-hidden="true" className="shrink-0" />
      <span>
        You are offline. The catalogue and calculators still work; product
        images will show a placeholder until you are back online.
      </span>
    </div>
  );
}
